import { useMemo, memo } from "react";
import { Atom } from "lucide-react";
import type { DataSourceStatusType } from "../lib/api";
import DatasourceCard from "./DatasourceCard";
import "./ProgressGrid.css";

export interface ProgressGridSource {
  id: string;
  name: string;
  region?: string;
  icon: React.ReactNode;
  status: DataSourceStatusType;
  message: string;
  duration?: number;
  progress?: number;
  errorDetail?: string;
  dataCount?: number;
  data?: Record<string, unknown>;
}

interface ProgressGridProps {
  sources: ProgressGridSource[];
  expandedCardId: string | null;
  onCardToggle: (cardId: string) => void;
  className?: string;
}

const STATUS_PRIORITY: Record<DataSourceStatusType, number> = {
  success: 1,
  not_found: 2,
  error: 3,
  running: 4,
  pending: 5,
};

export const DATASOURCE_NAMES: Record<string, string> = {
  cde_reflisted: "CDE 参比制剂目录",
  cde_trials: "CDE 临床试验",
  fda_drugs: "FDA (openFDA)",
  fda_guidance: "FDA PSG",
  fda_review: "FDA Review",
  ema: "EMA",
  pmda: "PMDA",
  health_canada: "Health Canada DPD",
  nmpa: "NMPA",
  pubmed: "PubMed",
  pubchem: "PubChem Molecular Structure",
  dailymed: "DailyMed",
  cde_consistency: "CDE 一致性评价",
};

export const DATASOURCE_REGIONS: Record<string, string> = {
  cde_reflisted: "CN",
  cde_trials: "CN",
  fda_drugs: "US",
  fda_guidance: "US",
  fda_review: "US",
  ema: "EU",
  pmda: "JP",
  health_canada: "CA",
  nmpa: "CN",
  pubmed: "Global",
  pubchem: "Global",
  dailymed: "US",
  cde_consistency: "CN",
};

export const DATASOURCE_ICONS: Record<string, React.ReactNode> = {
  cde_reflisted: "🇨🇳",
  cde_trials: "🔬",
  fda_drugs: "🇺🇸",
  fda_guidance: "📋",
  fda_review: "📝",
  ema: "🇪🇺",
  pmda: "🇯🇵",
  health_canada: "CA",
  nmpa: "🏥",
  pubmed: "📚",
  pubchem: <Atom className="h-4 w-4" aria-hidden="true" />,
  dailymed: "💊",
  cde_consistency: "✅",
};

function ProgressGrid({
  sources,
  expandedCardId,
  onCardToggle,
  className = "",
}: ProgressGridProps) {
  const sortedSources = useMemo(() => {
    return [...sources].sort((a, b) => {
      const priorityDiff = STATUS_PRIORITY[a.status] - STATUS_PRIORITY[b.status];
      if (priorityDiff !== 0) return priorityDiff;
      return a.name.localeCompare(b.name);
    });
  }, [sources]);

  const stats = useMemo(() => {
    const total = sources.length;
    const completed = sources.filter(
      s => s.status === "success" || s.status === "error" || s.status === "not_found"
    ).length;
    const success = sources.filter(s => s.status === "success").length;
    const error = sources.filter(s => s.status === "error").length;
    const running = sources.filter(s => s.status === "running").length;
    return { total, completed, success, error, running };
  }, [sources]);

  const percentage = stats.total > 0 ? Math.round((stats.completed / stats.total) * 100) : 0;

  return (
    <div className={`progress-grid-container ${className}`}>
      <div className="progress-grid-header">
        <h2 className="progress-grid-title">
          <span aria-hidden="true">📊</span> 查询进度
        </h2>
        <div className="progress-grid-stats">
          <span className="progress-grid-stat-item">
            完成: <strong>{stats.completed}/{stats.total}</strong>
          </span>
          <span className={`progress-grid-stat-item ${stats.success > 0 ? "stat-success" : ""}`}>
            ✓ {stats.success}
          </span>
          <span className={`progress-grid-stat-item ${stats.error > 0 ? "stat-error" : ""}`}>
            ✗ {stats.error}
          </span>
          {stats.running > 0 && (
            <span className="progress-grid-stat-item stat-running">
              🔄 {stats.running}
            </span>
          )}
        </div>
      </div>

      <div className="progress-grid-progress-bar-wrapper">
        <div className="progress-grid-progress-info">
          <span>总体进度</span>
          <strong>{percentage}%</strong>
        </div>
        <div className="progress-grid-progress-bar">
          <div
            className="progress-grid-progress-fill"
            style={{ width: `${percentage}%` }}
            role="progressbar"
            aria-valuenow={percentage}
            aria-valuemin={0}
            aria-valuemax={100}
          />
        </div>
      </div>

      <div className="progress-grid" role="list" aria-label="数据源查询状态列表">
        {sortedSources.map(source => (
          <DatasourceCard
            key={source.id}
            id={source.id}
            name={source.name}
            region={source.region}
            icon={source.icon}
            status={source.status}
            message={source.message}
            duration={source.duration}
            progress={source.progress}
            errorDetail={source.errorDetail}
            dataCount={source.dataCount}
            data={source.data}
            isExpanded={expandedCardId === source.id}
            onToggleExpand={() => onCardToggle(source.id)}
          />
        ))}
      </div>

      {sortedSources.length === 0 && (
        <div className="progress-grid-empty">
          <p>暂无数据源信息</p>
        </div>
      )}
    </div>
  );
}

export default memo(ProgressGrid);
